// OnboardingWizard Component
// Multi-step onboarding flow for new users

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { useNotification } from '../../context/NotificationContext';
import playerProfileService from '../../services/playerProfile/playerProfileService';
import styles from './OnboardingWizard.module.css';
import OnboardingProgress from './OnboardingProgress';

// Import step components
import WelcomeStep from './steps/WelcomeStep';
import BasicInfoStep from './steps/BasicInfoStep';
import AthleticInfoStep from './steps/AthleticInfoStep';
import BioStep from './steps/BioStep';
import CompletionStep from './steps/CompletionStep';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const OnboardingWizard = () => {
  const navigate = useNavigate();
  const { user, userProfile, setHasCompletedOnboarding } = useUser();
  const { showSuccess, showError } = useNotification();

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Helper to parse name from Firebase displayName
  const getFirstName = () => {
    if (userProfile?.firstName) return userProfile.firstName;
    if (user?.displayName) return user.displayName.split(' ')[0] || '';
    return '';
  };

  const getLastName = () => {
    if (userProfile?.lastName) return userProfile.lastName;
    if (user?.displayName) return user.displayName.split(' ').slice(1).join(' ') || '';
    return '';
  };

  // Form data for all steps
  const [formData, setFormData] = useState({
    // Screen 1: Welcome & Role
    userRole: 'player',
    
    // Screen 2: Basic Info (REQUIRED) - Pre-filled from Firebase/userProfile
    email: user?.email || userProfile?.email || '',
    firstName: getFirstName(),
    lastName: getLastName(),
    dateOfBirth: userProfile?.dateOfBirth || '',
    gender: userProfile?.gender || '',
    location: {
      city: userProfile?.location?.city || '',
      state: userProfile?.location?.state || '',
      country: userProfile?.location?.country || ''
    },
    
    // Screen 3: Athletic Info (REQUIRED)
    primarySport: '',
    positions: [],
    yearsExperience: '',
    currentLevel: '',
    
    // Screen 4: Bio (OPTIONAL)
    headline: '',
    bio: '',
  });

  const totalSteps = 5;
  const requiredSteps = 3;

  // Update form data when user/userProfile loads
  React.useEffect(() => {
    if (user || userProfile) {
      setFormData(prev => ({
        ...prev,
        email: user?.email || userProfile?.email || prev.email,
        firstName: getFirstName(),
        lastName: getLastName(),
        dateOfBirth: userProfile?.dateOfBirth || prev.dateOfBirth,
        gender: userProfile?.gender || prev.gender,
        location: {
          city: userProfile?.location?.city || prev.location.city,
          state: userProfile?.location?.state || prev.location.state,
          country: userProfile?.location?.country || prev.location.country
        }
      }));
    }
  }, [user, userProfile]);

  // Update form data
  const updateFormData = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  // Validate current step
  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.userRole !== '';
      
      case 2:
        return (
          formData.firstName.trim() !== '' &&
          formData.lastName.trim() !== '' &&
          formData.dateOfBirth !== '' &&
          formData.gender !== '' &&
          formData.location.city !== '' &&
          formData.location.country !== ''
        );
      
      case 3:
        if (formData.userRole === 'parent') return true;
        return (
          formData.primarySport !== '' &&
          formData.positions.length > 0 &&
          formData.currentLevel !== ''
        );
      
      case 4:
        // Optional step - always valid
        return true;
      
      case 5:
        // Completion screen - no validation
        return true;
      
      default:
        return false;
    }
  };

  // Handle Next button
  const handleNext = () => {
    if (!validateStep(currentStep)) {
      showError('Please fill in all required fields before continuing.');
      return;
    }

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  // Handle Back button
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Handle Skip (for optional steps)
  const handleSkip = () => {
    if (currentStep === 3 && formData.userRole === 'parent') {
      setCurrentStep(4);
    } else if (currentStep === 4) {
      setCurrentStep(5);
    }
  };

  // Handle final submission
  const handleComplete = async () => {
    setIsSubmitting(true);
    
    try {
      // Calculate age from date of birth
      const age = formData.dateOfBirth ? 
        new Date().getFullYear() - new Date(formData.dateOfBirth).getFullYear() : 
        null;

      const isParent = formData.userRole === 'parent';
      const hasAthleticInfo = formData.primarySport && formData.positions.length > 0;

      // Prepare profile data
      const profileData = {
        userId: userProfile._id,
        firstName: formData.firstName,
        lastName: formData.lastName,
        gender: formData.gender,
        dateOfBirth: formData.dateOfBirth,
        location: formData.location,
        isParentProfile: isParent,
        headline: formData.headline || (isParent
          ? 'Parent Profile'
          : `${formData.primarySport} ${formData.positions[0] || 'Player'}`),
        bio: formData.bio || '',
        athleticProfile: hasAthleticInfo ? {
          primarySport: formData.primarySport,
          secondarySports: [],
          positions: formData.positions.map((position, index) => ({
            sport: formData.primarySport,
            position: position,
            isPrimary: index === 0
          })),
          physicalAttributes: {},
          performanceMetrics: {}
        } : {
          primarySport: '',
          secondarySports: [],
          positions: [],
          physicalAttributes: {},
          performanceMetrics: {}
        },
        experience: (formData.yearsExperience && formData.currentLevel && formData.primarySport) ? [{
          type: 'achievement',
          title: `${formData.currentLevel.replace('_', ' ')} Level`,
          organization: formData.primarySport,
          description: `${formData.yearsExperience} of experience`,
          isCurrent: true,
          startDate: new Date(),
          verified: false
        }] : [],
        skills: {
          technical: [],
          tactical: [],
          physical: [],
          mental: []
        },
        media: {
          highlightVideos: [],
          photos: [],
          documents: []
        },
        availability: {
          status: 'not_looking',
          startDate: null,
          preferredLocations: [],
          willingToRelocate: false,
          travelRadius: null,
          preferredLevels: [],
          preferredCommitment: null,
          minimumCompensation: null,
          notes: ''
        },
        privacySettings: {
          basicInfo: 'public',
          athleticProfile: 'public',
          awards: 'public',
          clubHistory: 'clubs',
          events: 'clubs',
          skills: 'public',
          media: 'public',
          availability: 'clubs'
        }
      };

      // Create player profile
      await playerProfileService.createProfile(profileData);
      
      // Get auth token for API calls
      let authToken = null;
      try {
        authToken = await user.getIdToken();
      } catch (tokenError) {
        console.error('Error getting auth token:', tokenError);
      }

      const authHeaders = {
        'Content-Type': 'application/json',
        ...(authToken && { 'Authorization': `Bearer ${authToken}` })
      };

      // Update user's dashboard role based on selection
      try {
        await fetch(`${backendUrl}/api/users/${userProfile._id}/dashboard-role`, {
          method: 'PATCH',
          headers: authHeaders,
          body: JSON.stringify({
            dashboardRole: formData.userRole,
            addToAvailable: true
          })
        });
      } catch (roleError) {
        console.error('Error setting dashboard role:', roleError);
      }

      // Mark onboarding as completed on the server
      try {
        await fetch(`${backendUrl}/api/users/${userProfile._id}`, {
          method: 'PATCH',
          headers: authHeaders,
          body: JSON.stringify({ onboardingCompleted: true })
        });
      } catch (onboardingError) {
        console.error('Error marking onboarding complete:', onboardingError);
      }
      
      setHasCompletedOnboarding(true);
      
      // Show completion screen
      setCurrentStep(5);
      
      showSuccess('Welcome to SportLoop! Your profile has been created.');
    } catch (error) {
      console.error('Error creating profile:', error);
      showError('Failed to create profile. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle completion actions
  const handleViewProfile = () => {
    navigate(`/player-profile/${userProfile._id}`);
  };

  const handleGoToDashboard = () => {
    navigate('/');
  };

  // Render current step
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <WelcomeStep
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
          />
        );
      
      case 2:
        return (
          <BasicInfoStep
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      
      case 3:
        return (
          <AthleticInfoStep
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
            isOptional={formData.userRole === 'parent'}
            onSkip={handleSkip}
          />
        );
      
      case 4:
        return (
          <BioStep
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleComplete}
            onBack={handleBack}
            onSkip={handleSkip}
            isSubmitting={isSubmitting}
          />
        );
      
      case 5:
        return (
          <CompletionStep
            formData={formData}
            onViewProfile={handleViewProfile}
            onGoToDashboard={handleGoToDashboard}
          />
        );
      
      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.wizard}>
        {/* Progress Indicator (hide on completion screen) */}
        {currentStep < 5 && (
          <OnboardingProgress
            currentStep={currentStep}
            totalSteps={totalSteps}
          />
        )}

        {/* Step Content */}
        <div className={styles.stepContent}>
          {renderStep()}
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;

